import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import type { ILink } from '@platform/core/types';
import { defaultTabOf } from '../tabs/childRelations';
import { isLawTab } from '../tabs/lawdesigner/lawTabs';
import { useFileUploads } from '../tabs/files/useFileUploads';
import { visualizationOf } from '../tabs/visualization/visualizationOf';
import { canMoveStatus } from './keyline/statusTransitions';
import type { IRelatedItem } from './general/IRelatedItem';
import type { IRecordViewUiInput } from './IRecordViewUiInput';
import type { IMiniTarget } from './mini/IMiniTarget';
import type { IAdvisoryRecommendation } from './panels/IAdvisoryRecommendation';
import type { RecordPanel } from './RecordPanel';
import { isDoneStatus } from '@platform/engine/views/doneStatuses';

/** Фокус на елементі вкладки після переходу на неї (прототип: «+» коментарів ставить курсор у поле, «+» зв'язків — у пошук). */
const focusLater = (selector: string) => window.setTimeout(() => (document.querySelector(selector) as HTMLElement | null)?.focus(), 80);

/**
 * Стан сторінки запису поза чернеткою: бічна панель («Поради» / «Зіставлення» з модулем), міні-форма, навігатор зв'язків, перехід на
 * вкладки, «+» пігулок (зв'язати, написати коментар, прикріпити файли) і дії рекомендацій «Порад».
 */
export function useRecordViewUi({ module, rt, record, editor, actions, onTab }: IRecordViewUiInput) {
  const navigate = useNavigate();
  const uploads = useFileUploads(record);
  const [panel, setPanel] = useState<RecordPanel | null>(null);
  const [mapModule, setMapModule] = useState('');
  const [mini, setMini] = useState<IMiniTarget | null>(null);
  const [params] = useSearchParams();
  const [navigator, setNavigator] = useState(params.get('nav') === '1');
  const viz = Boolean(visualizationOf(module, rt, record));
  // Типова вкладка — без сегмента в адресі; вкладки конструктора закону — завжди явні (прототип: клік ставить tab «lz_…», і пункт
  // «Візуальна модель закону» підсвічується)
  const goTab = useCallback((key: string) => (onTab ? onTab(key)
    : navigate(routes.record(module.module.key, rt.key, record.id, key === defaultTabOf(rt, viz) && !isLawTab(key) ? undefined : key))),
  [onTab, navigate, module, rt, record.id, viz]);
  const addLink = () => { goTab('related'); focusLater('[data-link-finder]'); };
  const addComment = () => { goTab('comments'); focusLater('[data-comment-composer]'); };
  const attach = (files: File[]) => { void uploads.upload(files); goTab('files'); };
  const openMapping = (scope: string) => { setMapModule(scope); setPanel('mapping'); };
  const openLinked = (l: ILink) => setMini({ module: l.otherModule, recordType: l.otherRecordType, id: l.otherRecordId });
  const openItem = (i: IRelatedItem) => setMini({ module: i.module, recordType: i.recordType, id: i.id });
  const advisoryAction = (action: NonNullable<IAdvisoryRecommendation['action']>) => {
    if (action === 'assignMe') actions.assignMe();
    else if (action === 'openHistory') goTab('history');
    else if (action === 'addAssignment') goTab('assignments');
    else if (action === 'linkItem') addLink();
    else if (action === 'goFiles') goTab('files');
    else {
      // Як у прототипі: перший статус типу зі списку завершених DONE (не прапорець closed)
      const closed = rt.statuses.list.find((s) => isDoneStatus(s.value) && canMoveStatus(rt.statuses, record.status, s.value));
      if (closed) void editor.patchNow({ status: closed.value });
    }
  };
  return {
    panel, setPanel: (p: RecordPanel | null) => { setMapModule(''); setPanel(p); }, mapModule, mini, setMini, navigator, setNavigator,
    goTab, addLink, addComment, attach, openMapping, openLinked, openItem, advisoryAction, uploadError: uploads.error,
  };
}
